/**
 * Why a picked file was dropped rather than uploaded.
 *
 * Ordered as the checks run, which is also the order the toasts appear in when
 * one selection breaks more than one rule.
 */
export const PHOTO_REJECTION = {
  UNSUPPORTED_FORMAT: "unsupported_format",
  TOO_LARGE: "too_large",
  LIMIT_REACHED: "limit_reached",
} as const;

export type PhotoRejection =
  (typeof PHOTO_REJECTION)[keyof typeof PHOTO_REJECTION];

export interface IPhotoSelection {
  /** What actually goes to the uploader. */
  accepted: File[];
  /** One entry per rule the selection broke — never one per file. */
  rejected: PhotoRejection[];
}

export interface IPhotoSelectionRules {
  /** Lowercase, without the dot. Empty means the caller declared none. */
  allowedExtensions: string[];
  maxFileSizeBytes: number;
  /** `maxFiles` minus what is already added or still uploading. */
  availableSlots: number;
}

/**
 * The extension, which is what both ends actually judge a photo by:
 * `BuildPhotoAttachment` refuses to presign an extension outside
 * `PhotoAttachment::ALLOWED_EXTENSIONS`, and the photo book validates the same
 * way. A file's MIME type never enters into it.
 */
const extensionOf = (file: File): string =>
  file.name.split(".").pop()?.toLowerCase() ?? "";

/**
 * Splits a picker selection into what may be uploaded and the reasons the rest
 * was dropped.
 *
 * Pure, and deliberately reason-per-rule rather than reason-per-file: the copy
 * says "Picture(s) …", so one broken rule is one toast however many files
 * broke it. A file that breaks two rules is only reported against the first —
 * checks run format, then size, then the remaining slots, each narrowing what
 * the next one sees.
 */
export const selectUploadablePhotos = (
  files: File[],
  { allowedExtensions, maxFileSizeBytes, availableSlots }: IPhotoSelectionRules
): IPhotoSelection => {
  const rejected: PhotoRejection[] = [];

  // No declared extensions is no restriction, not "reject everything" — the
  // photos screen reads them off `constraints`, which a category config may
  // leave out, and the backend still has the final say either way.
  const supported = allowedExtensions.length
    ? files.filter((file) => allowedExtensions.includes(extensionOf(file)))
    : files;

  if (supported.length < files.length)
    rejected.push(PHOTO_REJECTION.UNSUPPORTED_FORMAT);

  const withinSize = supported.filter((file) => file.size <= maxFileSizeBytes);

  if (withinSize.length < supported.length)
    rejected.push(PHOTO_REJECTION.TOO_LARGE);

  const accepted = withinSize.slice(0, Math.max(availableSlots, 0));

  if (accepted.length < withinSize.length)
    rejected.push(PHOTO_REJECTION.LIMIT_REACHED);

  return { accepted, rejected };
};
